import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ChannelSelections, NotificationContent, CommsChannel, CommsType, Location, SmsMessage, EmailMessage, Call} from '../../models/strategySandbox';
import {equal, isEmpty, omit} from '../../utils';
import {CallsState, State} from './types';

const DEFAULT_LOCATION: Location = {
    screen: 'home'
};

const DEFAULT_CALLS: CallsState = {
    active: null,
    history: []
};

const initialState: State = {
    selections: {},
    activeNotification: null,
    notifications: [],
    location: DEFAULT_LOCATION,
    smsList: [],
    emailList: [],
    calls: DEFAULT_CALLS
};

const slice = createSlice({
    name: 'strategySandbox',
    initialState,
    reducers: {
        setSelection(state, action: PayloadAction<{channel: CommsChannel; commsType: CommsType | null}>) {
            const {channel, commsType} = action.payload;

            state.selections = equal(commsType, null)
                ? omit(state.selections, [channel])
                : {...state.selections, [channel]: commsType};
        },
        setSelections(state, action: PayloadAction<ChannelSelections>) {
            state.selections = action.payload;
        },
        showNotification(state, action: PayloadAction<NotificationContent>) {
            state.activeNotification = action.payload;
            state.notifications.push(action.payload);
        },
        hideNotification(state) {
            state.activeNotification = null;
        },
        clearNotifications(state) {
            state.notifications = [];
            state.activeNotification = null;
        },
        navigateTo(state, action: PayloadAction<Location>) {
            state.location = action.payload;
        },
        navigateBack(state) {
            const {tabPath = []} = state.location;

            if (!isEmpty(tabPath)) {
                state.location = {
                    ...state.location,
                    tabPath: tabPath.slice(0, -1)
                };
            }
        },
        pushTabPath(state, action: PayloadAction<string>) {
            const {tabPath = []} = state.location;

            state.location = {
                ...state.location,
                tabPath: [...tabPath, action.payload]
            };
        },
        resetNavigation(state) {
            state.location = DEFAULT_LOCATION;
        },
        addSmsMessage(state, action: PayloadAction<SmsMessage>) {
            state.smsList = [action.payload, ...state.smsList];
        },
        markSmsAsRead(state, action: PayloadAction<number>) {
            state.smsList = state.smsList.map(sms => (sms.id === action.payload ? {...sms, isRead: true} : sms));
        },
        addEmailMessage(state, action: PayloadAction<EmailMessage>) {
            state.emailList = [action.payload, ...state.emailList];
        },
        markEmailAsRead(state, action: PayloadAction<number>) {
            state.emailList = state.emailList.map(email => (email.id === action.payload ? {...email, isRead: true} : email));
        },
        startIncomingCall(state, action: PayloadAction<string>) {
            state.calls.active = {status: 'incoming', callerName: action.payload};
        },
        acceptCall(state) {
            if (state.calls.active) {
                state.calls.active.status = 'active';
                state.calls.active.startTime = Date.now();
            }
        },
        endCall(state, action: PayloadAction<Call | undefined>) {
            if (action.payload) {
                state.calls.history = [action.payload, ...state.calls.history];
            }
            state.calls.active = null;
        },
        addCallToHistory(state, action: PayloadAction<Call>) {
            state.calls.history = [action.payload, ...state.calls.history];
        }
    },
    selectors: {
        getSelections: state => state.selections,
        getActiveNotification: state => state.activeNotification,
        getNotifications: state => state.notifications,
        getLocation: state => state.location,
        getSmsList: state => state.smsList,
        getEmailList: state => state.emailList,
        getCalls: state => state.calls
    }
});

export const {setSelection, setSelections, showNotification, hideNotification, clearNotifications, navigateTo, navigateBack, pushTabPath, resetNavigation, addSmsMessage, markSmsAsRead, addEmailMessage, markEmailAsRead, startIncomingCall, acceptCall, endCall, addCallToHistory} = slice.actions;
export const {getSelections, getActiveNotification, getNotifications, getLocation, getSmsList, getEmailList, getCalls} = slice.selectors;
export default slice.reducer;
